/**
 * Rovo Credits — content bundle for the WAC UBP calculator.
 *
 * Source of truth: Confluence "Rovo: Content spec for WAC Calculator"
 * https://hello.atlassian.net/wiki/spaces/acbp/pages/7129272798
 *
 * Content DRI: Belle Craig · Meter CD: Melissa Jaén
 *
 * Fields we haven't finalized are simply OMITTED from this file (most
 * are optional at the type level) so nothing renders for them. Do NOT
 * stub placeholder strings — omit the field entirely instead. There is
 * no `PLACEHOLDER` / `todoCopy` helper exported from this module by
 * design.
 *
 * DO NOT edit unrelated meter files when updating this one — the atomic
 * per-meter file split exists to prevent merge conflicts between authors.
 *
 * i18n: every copy field is wrapped in `defineMessage` with a literal
 * `id` and `defaultMessage`. The extractor is a static AST analyzer —
 * see the i18n contract in `meter-content.types.ts`. Keep both fields
 * literal; never build an id from a variable.
 */

import { defineMessage } from 'react-intl';

import { wacToken } from '@/libraries/design/foundations';

import type { MeterContent } from './meter-content.types';

export const ROVO_CREDITS_CONTENT: MeterContent = {
	description: defineMessage({
		id: 'ubp.calculator.usage.rovoCredits.description',
		defaultMessage:
			'Get fast answers, run powerful AI agents, and connect your organizational context to any AI tool.',
	}),
	needsStep: {
		// Step 2 "Do you know your usage?" question — Rovo copy.
		doYouKnowQuestion: defineMessage({
			id: 'ubp.calculator.needs.rovoCredits.doYouKnowQuestion',
			defaultMessage: 'Know how many credits you’ll need?',
		}),
	},
	estimateStep: {
		subtitle: defineMessage({
			id: 'ubp.calculator.estimate.rovoCredits.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many Rovo credits you need, and how you can use them.',
		}),
		compareCta: defineMessage({
			id: 'ubp.calculator.estimate.rovoCredits.compareCta',
			defaultMessage: 'Explore plans',
		}),
		// TEMPORARY: All meters currently share this Orange Logic asset
		// while the content team finalizes per-meter hero imagery. Track
		// replacement in the follow-up content ticket.
		image: {
			src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/bb1q20l5xjp2i85j55ou317euquo3rv3.png',
			alt: '',
			imageBackgroundColor: wacToken('color.extended.blue100'),
		},
		// ContentBlock copy per Rovo Confluence spec (Step 3 right-column
		// "Compare value" panel).
		contentBlock: {
			// Copy per revised design direction (2026-07-16): reframe the
			// Card 1 messaging around ROI + built-in allowances, using
			// the "Explore plans" CTA (paired with `compareCta` above).
			title: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.contentBlock.title',
				defaultMessage: 'Maximize your ROI with flexible, usage-based plans',
			}),
			intro: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.contentBlock.intro',
				defaultMessage:
					'With Atlassian collections and apps, built-in allowances align your costs with your real-world usage.',
			}),
			listItems: [
				defineMessage({
					id: 'ubp.calculator.estimate.rovoCredits.contentBlock.listItems.0',
					defaultMessage: 'Optimize your spend',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.rovoCredits.contentBlock.listItems.1',
					defaultMessage: 'Plan with confidence',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.rovoCredits.contentBlock.listItems.2',
					defaultMessage: 'Do more with less',
				}),
			],
			href: '/collections',
		},
	},
	allowanceStep: {
		// Step 4 primary CTA — "Compare" per the latest design direction
		// (all meters share the comparison-focused verb on this step so
		// users understand the Summary step surfaces a comparison view).
		nextCta: defineMessage({
			id: 'ubp.calculator.allowance.rovoCredits.nextCta',
			defaultMessage: 'Compare',
		}),
	},
	summaryStep: {
		// Step 5 header title — currently uniform across all meters per
		// spec. Kept per-meter for future flexibility (content team may
		// promote per-meter variants later, e.g. "Review your Rovo plan").
		title: defineMessage({
			id: 'ubp.calculator.summary.rovoCredits.title',
			defaultMessage: 'Review usage estimate and compare',
		}),
		subtitle: defineMessage({
			id: 'ubp.calculator.summary.rovoCredits.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many Rovo credits you need, and how you can use them.',
		}),
		// Rovo shows BOTH cards (org needs + what's covered) per
		// Figma node 2055:13212 & the Rovo Confluence spec.
		// Only the eyebrow + subtext are static — the `headline` is
		// injected at render time by summary-step.tsx from the live
		// usage-range calculation, so it's intentionally omitted here.
		// `body` and `footerNote` are omitted until content is
		// finalized (the ResultsCard renders without them).
		orgNeedsCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.rovoCredits.orgNeedsCard.eyebrow',
				defaultMessage: 'YOUR ORGANIZATION NEEDS',
			}),
			headlineSubtext: defineMessage({
				id: 'ubp.calculator.summary.rovoCredits.orgNeedsCard.headlineSubtext',
				defaultMessage: 'Rovo credits monthly',
			}),
		},
		// "What's covered" card (Rovo Confluence spec Step 5, Card 4).
		// This card is DYNAMIC based on whether the org's estimated usage
		// fits within its included allowance:
		//   - headline       → live included allowance (e.g. "200,000")
		//   - headlineSubtext→ "Rovo credits included monthly"
		//   - covered case   → bodyCovered + listItemsCovered (value props)
		//   - overage case   → bodyOverage + listItemsOverage (guidance)
		// The `{overage}` / `{cost}` tokens in bodyOverage are replaced at
		// render time by summary-step.tsx with the live extra-usage range
		// and estimated extra monthly charge.
		whatsCoveredCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.eyebrow',
				defaultMessage: 'WHAT’S COVERED',
			}),
			headlineSubtext: defineMessage({
				id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.headlineSubtext',
				defaultMessage: 'Rovo credits included monthly',
			}),
			bodyCovered: defineMessage({
				id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.bodyCovered',
				defaultMessage: 'Your Rovo credits are covered within the allowance.',
			}),
			bodyOverage: defineMessage({
				id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.bodyOverage',
				defaultMessage:
					'There’s a gap of {overage} Rovo credits. You may incur extra usage charges of {cost}.',
			}),
			listItemsCovered: [
				defineMessage({
					id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.listItemsCovered.0',
					defaultMessage: 'Quick answers',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.listItemsCovered.1',
					defaultMessage: 'Extended tasks',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.listItemsCovered.2',
					defaultMessage: 'Custom agents',
				}),
			],
			listItemsOverage: [
				defineMessage({
					id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.listItemsOverage.0',
					defaultMessage: 'Higher plans provide more allowances',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.listItemsOverage.1',
					defaultMessage:
						'Track your extra usage via your capabilities subscription details page in admin hub',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.rovoCredits.whatsCoveredCard.listItemsOverage.2',
					defaultMessage: 'Set an extra usage limit to stay in budget',
				}),
			],
		},
		// Per revised design (2026-07-16): Rovo does NOT show the
		// Monthly Cost Estimate card. Only CSR and Assets do.
	},
	usageProfiles: {
		// Bullets (`list`) and value props (`valueProp`) per the finalized
		// Rovo Confluence spec Step 2 (Meter Usage Profiles). `usageRange`
		// is not displayed on the Step 2 cards themselves (per spec note
		// "not displayed in this step") but drives the usage estimate.
		exploring: {
			label: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.exploring.label',
				defaultMessage: 'Early exploration',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.exploring.description',
				defaultMessage:
					'We’re testing the waters — using AI for the occasional search, summary, or draft to see where it adds value.',
			}),
			usageRange: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.exploring.usageRange',
				defaultMessage: '100–500 per user per month',
			}),
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.exploring.list.0',
					defaultMessage: 'A few Chat searches and summaries',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.exploring.list.1',
					defaultMessage: 'The occasional complex research task',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.exploring.list.2',
					defaultMessage: 'Trying out an agent once or twice',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.exploring.valueProp',
				defaultMessage:
					'Teams in the early exploration profile typically save up to 2 hours per month per user.',
			}),
		},
		adopting: {
			label: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.adopting.label',
				defaultMessage: 'Routine adoption',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.adopting.description',
				defaultMessage:
					'AI is part of how we work each week — drafting content, catching up on updates, and automating repetitive steps across projects.',
			}),
			usageRange: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.adopting.usageRange',
				defaultMessage: '500–2,000 per user per month',
			}),
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.adopting.list.0',
					defaultMessage: 'Chat most days for quick answers',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.adopting.list.1',
					defaultMessage: 'Weekly research or analysis in Think Deeper',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.adopting.list.2',
					defaultMessage: 'Agents handling recurring tasks',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.adopting.valueProp',
				defaultMessage:
					'Teams in the routine adoption profile typically save 5–6 hours per month, per user.',
			}),
		},
		scaling: {
			label: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.scaling.label',
				defaultMessage: 'Transformative usage',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.scaling.description',
				defaultMessage:
					'Multiple teams rely on AI daily for planning, decision-making, and connecting knowledge across tools.',
			}),
			usageRange: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.scaling.usageRange',
				defaultMessage: '2,000–4,000 per user per month',
			}),
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.scaling.list.0',
					defaultMessage: 'AI embedded in daily workflows across teams',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.scaling.list.1',
					defaultMessage: 'Agents running autonomously in automations',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.rovoCredits.scaling.list.2',
					defaultMessage: 'Complex research and planning on demand',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.rovoCredits.scaling.valueProp',
				defaultMessage:
					'Teams in the transformative usage profile typically save 1–2 days per month, per user.',
			}),
		},
	},
	valueProps: [
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.valueProps.0.title',
				defaultMessage: 'Quick answers',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.valueProps.0.description',
				defaultMessage:
					'Get quick insights, summaries, and recommendations from Rovo Chat, without leaving your workflow.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.valueProps.1.title',
				defaultMessage: 'Extended tasks',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.valueProps.1.description',
				defaultMessage:
					'Dynamically plan, search, reason, and iterate to return a thorough answer with Think deeper mode.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.valueProps.2.title',
				defaultMessage: 'Custom agents',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.rovoCredits.valueProps.2.description',
				defaultMessage:
					'Power specialized agents for tasks like grooming Jira backlogs, summarizing Confluence spaces, or drafting internal comms on behalf of specific teams.',
			}),
		},
	],
};
